/**
 * THE OVERLAY BAKE MUST COME OUT THE SHAPE OF THE BLOCK (2026-10-09, Jack: "ensure the render
 * result looks good from the picture and it's not stretched").
 *
 * Measured on Jack's own test: the baker produced 1800×1416 (ratio 1.27) and ECH_MainImage draws
 * it at a locked 500×350 (ratio 1.43), so the photo, the sky and the lockup were squashed ~11%.
 * The baker is the old form's code (vendor/ovSky.js, verbatim) and it already has the right tool:
 * HERO BOX mode, `ovBoxH`, which composes into a FIXED frame and cover-crops the photo into it.
 * The React card simply never told it the block's size. So, from here, every bake is given the
 * frame of the block it lands in:
 *
 *   frame height = 600 × slot.h / slot.w      (the baker always works 600 wide, at 3×)
 *
 * One case the box alone cannot cover: text BELOW the image ("Bottom") grows the canvas by a white
 * band under the frame. The band's height depends only on the words and the size, never on the
 * box, so after the first bake the overshoot is measured and the box is shrunk by exactly that
 * much — one corrective bake, after the first one has finished hosting so the two can't race.
 *
 * The same session re-bakes, from the pristine photo, when the requester changes the block's
 * Height or replaces the photo — both used to leave the old bake in place, squashed into the new
 * slot or sitting on the wrong picture.
 */
import { __ovCommit, setOvLiveHook, type OvModel } from "../vendor/ovSky";
import { ovActive } from "./heroOverlay";
import { bynderBase, isBynderTransform, parseDims, viewFromUrl, type Dims } from "./bynderCrop";
import { installOverlayFx } from "./overlayFx";

export interface Slot { w: number; h: number }

/** the baker works at 600 px wide; the frame that matches a w×h slot */
export function frameFor(slot: Slot | null | undefined): number {
  if (!slot || !(slot.w > 0) || !(slot.h > 0)) return 0;
  return Math.round((600 * slot.h) / slot.w);
}
export const slotKey = (slot: Slot | null | undefined): string => (slot && slot.w && slot.h ? slot.w + "x" + slot.h : "");

/* ── SKY NEVER CUTS OFF THE HOUSE (2026-10-10, Jack: "you cannot cut off the home") ──────────
   In the fixed frame, the old form makes room for sky by pushing the photo down, which crops the
   bottom of the house (measured: at sky 45 the house dropped to the bottom edge). The fix widens
   the requester's own Bynder crop sideways, keeping its top and bottom rows exactly, until the
   photo is the frame's width at the height left under the sky. Bynder serves that wider slice,
   the baker draws it full-width under the band, and nothing from the house is lost. A photo that
   cannot be widened that far (its edges reached) gets the most sky that still fits; a photo that
   is not a Bynder transform cannot be widened at all, so it gets none. */
export const SKY_CAP = 0.33;   /* the baker's own ceiling on a forced band (vendor: Math.round(H*0.33)) */

export interface SkyPlan { url: string; pct: number; skyPx: number; region: { x: number; y: number; w: number; h: number } }

function gravityCrop(r: { x: number; y: number; w: number; h: number }): string {
  const first = "io=transform:crop,width:" + (r.x + r.w) + ",height:" + (r.y + r.h) + ",gravity:topleft";
  if (r.x === 0 && r.y === 0) return first;
  return first + "&io=transform:crop,width:" + r.w + ",height:" + r.h + ",gravity:bottomright";
}

/**
 * The base the baker should use for `pct`% sky on the requester's crop `pick` — or null when the
 * photo cannot carry any sky without losing part of the house.
 */
export function planSky(pick: string, nat: Dims | null, slot: Slot | null | undefined, pct: number, boxH?: number): SkyPlan | null {
  /* the photo's frame: the block's own, or the smaller box left when copy sits below the photo */
  const frame = boxH && boxH > 0 ? Math.round(boxH) : frameFor(slot);
  if (!frame || !slot || !nat || !isBynderTransform(pick)) return null;
  const v = viewFromUrl(pick, nat, slot);
  const x = -v.ox / v.s, y = -v.oy / v.s, w = slot.w / v.s, h = slot.h / v.s;
  let skyPx = pct > 0 ? Math.min(Math.round((frame * Math.min(45, pct)) / 100), Math.round(frame * SKY_CAP)) : 0;
  if (skyPx > 0 && (h * 600) / (frame - skyPx) > nat.w) skyPx = Math.max(0, Math.floor(frame - (600 * h) / nat.w));
  /* the slider is whole percents and the baker rounds frame×pct, so settle on a whole percent first
     and size the slice for exactly the band the baker will draw — never a pixel more */
  const whole = Math.max(0, Math.floor((100 * skyPx) / frame));
  skyPx = Math.round((frame * whole) / 100);
  const Hp = frame - skyPx;
  /* already the right shape (no sky, full box): the requester's crop is used as it is */
  if (Hp >= (600 * h) / w - 0.5) return { url: pick, pct: whole, skyPx, region: { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) } };
  let w2 = (h * 600) / Hp, h2 = h, y2 = y;
  if (w2 > nat.w) {
    /* copy below a narrow photo: the slice cannot get wider, so it gets shorter — from the TOP, which
       on an exterior is sky; the ground line and the house's base are kept */
    w2 = nat.w; h2 = (w2 * Hp) / 600; y2 = y + h - h2;
  }
  const x2 = Math.max(0, Math.min(nat.w - w2, x + w / 2 - w2 / 2));
  const region = { x: Math.round(x2), y: Math.max(0, Math.round(y2)), w: Math.min(nat.w, Math.round(w2)), h: Math.min(nat.h, Math.round(h2)) };
  if (region.x + region.w > nat.w) region.x = nat.w - region.w;
  if (region.y + region.h > nat.h) region.y = nat.h - region.h;
  /* served at the slice's own pixels (never upscaled past 2400 wide) so the bake stays sharp */
  const outW = Math.min(2400, region.w), outH = Math.round((outW * region.h) / region.w);
  const url = bynderBase(pick) + "?" + gravityCrop(region) + "&io=transform:fill,width:" + outW + ",height:" + outH;
  return { url, pct: whole, skyPx, region };
}

/**
 * Point the model at the right photo for its sky setting. Keeps the requester's own crop in
 * `ovPick` so sky 0 (or a reposition) always starts from it, never from a widened slice.
 * Returns the sky % actually applied (may be lower than asked, or 0).
 */
export function applySkyBase(m: OvModel, k: string, slot: Slot | null | undefined): number {
  const d = m.data;
  const cur = String(d[k + "__ovbase"] || (isBake(d[k]) ? "" : d[k]) || "");
  /* a remembered crop belongs to ONE photo — after a replace it is stale (measured 2026-10-10: the
     previous photo's crop carried over and the sky was planned, and baked, on the wrong house) */
  if (d.ovPick && cur && bynderBase(d.ovPick) !== bynderBase(cur)) delete d.ovPick;
  const pick = String(d.ovPick || cur);
  const want = +String(d.ovSky || 0);
  const restore = () => { if (d.ovPick) { d[k + "__ovbase"] = d.ovPick; delete d.ovPick; } };
  const plan = pick ? planSky(pick, parseDims(d[k + "__nat"]), slot, want, +String(d.ovBoxH || 0)) : null;
  if (!plan) {
    /* not a Bynder photo (or its size is unknown): it cannot be widened, so no sky — sky here could
       only be made by cropping the house */
    restore();
    if (want > 0) d.ovSky = "0";
    return 0;
  }
  if (plan.url === pick) restore();
  else { d.ovPick = pick; d[k + "__ovbase"] = plan.url; }
  if (want > 0) {
    d.ovSky = String(plan.pct);
    if (plan.pct > 0) { d.ovForce = "1"; d.ovSkyTouched = "1"; }
  }
  return plan.pct;
}

/** a value the baker produced (not a photo someone picked) */
export const isBake = (u: unknown): boolean => /^data:image\//i.test(String(u || "")) || /\/\/intel\.lennar-hub\.com\/hero-img\//i.test(String(u || ""));

/** with copy UNDER the photo the hero may grow — "a bit": at most this × the block's height */
export const TALL_MAX = 1.5;

/** the smallest photo frame worth keeping when copy below the image eats into a short slot */
export const MIN_FRAME = 120;

/** how far a baked image is off its slot, in px at the 600-wide scale (0 = exact) */
export function overshoot(natW: number, natH: number, frame: number): number {
  if (!(natW > 0) || !(natH > 0) || !(frame > 0)) return 0;
  return Math.round((natH * 600) / natW) - frame;
}

/** the box that makes "photo + band" come out exactly `frame` tall; null when no correction is possible */
export function correctedBox(curBox: number, over: number): number | null {
  if (Math.abs(over) <= 1) return null;
  const next = curBox - over;
  return next >= MIN_FRAME ? next : null;
}

function natSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((res) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => res({ w: 0, h: 0 });
    im.src = url;
  });
}

export interface Session {
  m: OvModel;
  /** call when the editor modal closes (Cancel reports nothing on its own) */
  sync: () => void;
}

/**
 * Wire a model to the block: every report from the baker copies `m.data` back (via `push`), and the
 * first finished bake is checked against the slot and corrected once if text below the image made
 * it taller. `m.data.ovBoxH` / `ovSlot` are set from the slot before anything bakes.
 */
export function startSession(m: OvModel, k: string, slot: Slot | null | undefined, push: (patch: Record<string, unknown>) => void, latest: () => Record<string, unknown>, onMisfit?: (msg: string) => void): Session {
  installOverlayFx();   /* the drawn confetti layer replaces the grainy photo one (services/overlayFx.ts) */
  const frame = frameFor(slot);
  if (frame) { m.data.ovBoxH = String(frame); m.data.ovSlot = slotKey(slot); }
  applySkyBase(m, k, slot);
  /* COPY THAT CANNOT FIT UNDER THE PHOTO (measured 2026-10-09: a 3-line headline + sub + button
     below a 500×350 hero needs 375 of its 420 px, leaving a 45 px photo). There is no honest
     picture to ship, so the block keeps the plain photo and the card says why — a squashed bake
     is never written. Every later report of that same bake is reverted too, because hosting
     lands after the check. */
  let blocked = false;
  /* the pristine photo, remembered — the stash is deleted on a revert, and the hosted URL of the
     same bake lands afterwards and must be reverted too */
  /* (the requester's own crop when sky widened the base — `ovPick` — a widened slice is the wrong
     shape for the block and must never be what is left on it) */
  let pristine = String(m.data.ovPick || m.data[k + "__ovbase"] || (isBake(m.data[k]) ? "" : m.data[k] || ""));
  const sync = () => {
    if (m.data.ovPick) pristine = String(m.data.ovPick);
    else if (m.data[k + "__ovbase"]) pristine = String(m.data[k + "__ovbase"]);
    if (blocked && isBake(m.data[k])) m.data[k] = pristine || m.data[k];
    /* the old form's own "nothing on" path restores the stash itself — if that stash is a widened
       sky slice, put the requester's crop back instead */
    if (!ovActive(m.data, k) && m.data.ovPick && !isBake(m.data[k]) && m.data[k] !== m.data.ovPick) { m.data[k] = m.data.ovPick; delete m.data.ovPick; }
    /* EVERYTHING REMOVED = THE ORIGINAL PHOTO, not a re-baked copy of it. The fixed frame
       (`ovBoxH`) counts as "something to bake" in the old form's short-circuit, so clearing
       every control still produced a hosted JPEG — which dropped the Bynder asset link from
       Main Image Info and re-hosted a plain photo at submit. */
    if (!ovActive(m.data, k) && isBake(m.data[k]) && pristine) { m.data[k] = pristine; delete m.data[k + "__ovbase"]; delete m.data.ovPick; delete m.data.ovDrawH; }
    const cur = latest(); const patch: Record<string, unknown> = {};
    Object.keys(m.data).forEach((key) => { if (m.data[key] !== cur[key]) patch[key] = m.data[key]; });
    Object.keys(cur).forEach((key) => { if (!(key in m.data) && cur[key] !== "" && cur[key] != null) patch[key] = ""; });
    if (Object.keys(patch).length) push(patch);
  };
  let checkedFor = ""; let fixArmed = false; let fixes = 0; let timer: ReturnType<typeof setTimeout> | null = null;
  /* every corrective bake starts a new epoch; a measurement that began in an older one is about an
     image that is already being replaced (measured 2026-10-10: the first bake's hosted copy resolved
     after the fix had started, triggered a SECOND fix against the wrong box, and the drawn height
     was stamped for an image that never shipped) */
  let epoch = 0;
  /* images that are already being replaced — never measured again. The old form reports a hosted
     bake twice (esLive, then esRecord), so the second report of the bake a fix just replaced arrived
     in the NEW epoch and triggered a second fix (measured live 2026-10-10: 749 → box 301 → 182). The
     image already on the block when the session opens belongs to the previous look, too. */
  const stale = new Set<string>();
  if (isBake(m.data[k])) stale.add(String(m.data[k]));
  const runFix = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!fixArmed) return;
    stale.add(String(m.data[k] || "")); stale.add(checkedFor);
    fixArmed = false; fixes++; epoch++;
    applySkyBase(m, k, slot);   /* a different box needs a re-sized slice of the photo */
    __ovCommit(m, k, null);
  };
  const armFix = () => {
    fixArmed = true;
    if (/^https?:/i.test(String(m.data[k] || ""))) runFix();          /* already hosted */
    else timer = setTimeout(runFix, 21000);                               /* hosting gives up at 20s */
  };
  setOvLiveHook((mm) => {
    if (mm && mm !== m) return;
    sync();
    const v = String(m.data[k] || "");
    if (!frame || !slot || !isBake(v)) return;
    if (fixArmed && /^https?:/i.test(v)) { runFix(); return; }   /* the previous bake finished hosting — safe to re-bake now */
    if (fixes >= 2 || fixArmed || checkedFor === v || stale.has(v)) return;
    checkedFor = v;
    const at = epoch;
    natSize(v).then(({ w, h }) => {
      if (at !== epoch || fixArmed) return;                 /* stale, or a fix is already queued */
      if (!(w > 0) || !(h > 0)) return;
      const total = Math.round((h * 600) / w);          /* the bake's height at the 600-wide scale */
      const box = +String(m.data.ovBoxH || frame);
      const maxTotal = Math.round(frame * TALL_MAX);
      /* 1 · a preset can carry its own box (Email Refresh: 520) — the PHOTO always gets the block's frame */
      if (box > frame) { m.data.ovBoxH = String(frame); armFix(); return; }
      /* 2 · text on the photo: exactly the block's shape */
      if (total <= frame + 1) { if (m.data.ovDrawH) { delete m.data.ovDrawH; sync(); } return; }
      /* 3 · TEXT UNDERNEATH MAKES THE HERO TALLER (2026-10-10, Jack: "if there's text underneath it
             can be a bit taller"). The photo keeps the block's full shape and the copy band is added
             below it; the block draws the image at that taller height (`ovDrawH`, applied to the
             height token at render — echRegistry). "A bit": up to TALL_MAX × the block's height. */
      if (total <= maxTotal + 1) { m.data.ovDrawH = String(Math.round((slot.h * total) / frame)); sync(); return; }
      /* 4 · more copy than that: the photo area gives up height (widened, never cropping the house —
             applySkyBase) so photo + copy = the cap; past MIN_FRAME there is no honest picture */
      const next = maxTotal - (total - box);
      if (next < MIN_FRAME) {
        blocked = true; delete m.data.ovDrawH; sync();
        if (onMisfit) onMisfit("This much copy doesn't fit under a " + slot.w + "×" + slot.h + " photo — shorten it, or set Position to Top.");
        return;
      }
      m.data.ovBoxH = String(next);
      m.data.ovDrawH = String(Math.round((slot.h * maxTotal) / frame));
      armFix();
    });
  });
  return { m, sync };
}

/** re-bake an existing overlay from its pristine photo, outside the editor (Height changed, photo replaced) */
export function rebake(data: Record<string, unknown>, k: string, slot: Slot | null | undefined, push: (patch: Record<string, unknown>) => void, latest: () => Record<string, unknown>, photo?: string, onMisfit?: (msg: string) => void): Session {
  const m: OvModel = { type: "echimg", data: { ...data } } as OvModel;
  if (photo) { m.data[k + "__ovbase"] = photo; m.data[k] = photo; delete m.data.ovPick; }
  const s = startSession(m, k, slot, push, latest, onMisfit);
  __ovCommit(m, k, null);
  return s;
}
